import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { FarmerServiceServiceProxy, FarmerDto, CountyDto, CountyServiceServiceProxy, WatershedServiceServiceProxy, WatershedDto, ExportRequestDto } from '@shared/service-proxies/service-proxies';
import { AlertController, LoadingController, ModalController } from '@ionic/angular';
import { AppStorageService } from '@shared/storage/app-storage.service';
import { Router, NavigationExtras } from '@angular/router';
import { PermissionCheckerService } from '@shared/auth/permission-checker.service';
import { ExportService } from '@shared/export/export.service';
import { Title } from '@angular/platform-browser';
import { Page } from 'src/model/page';
import { ColumnMode, DatatableComponent } from '@swimlane/ngx-datatable';
import * as moment from 'moment'; 
import { ProfilePage } from './profile/profile.page';

@Component({
  selector: 'app-farmers',
  templateUrl: './farmers.page.html',
  styleUrls: ['./farmers.page.scss'],
})
export class FarmersPage implements OnInit {

  rows: FarmerDto[];
  page = new Page();
  ColumnMode = ColumnMode;
  sortTerm: string = 'farmName';
  sortDirection: string = 'ASC';
  name: string;
  hasPassedCertification: number;
  counties: CountyDto[] = [];
  countyIds: number[] = [];
  watersheds: WatershedDto[] = [];
  watershedIds: number[] = [];
  hasProfileMissingFields: boolean;
  hasProfileErrors: boolean;
  isLoaded: boolean = false;
  @ViewChild('datatable', { static: false }) datatable: DatatableComponent;
  selected = [];
  constructor(
    private farmerSvc: FarmerServiceServiceProxy,
    private alertController: AlertController,
    storageService: AppStorageService,
    private router: Router,
    private countySvc: CountyServiceServiceProxy,
    private watershedSvc: WatershedServiceServiceProxy,
    private permissionChecker: PermissionCheckerService,
    private exportService: ExportService,
    private titleService: Title,
    private modalController: ModalController,
    private cdr: ChangeDetectorRef,
    public loadingController: LoadingController
  ) {
    this.titleService.setTitle('OACI - Farmers List');
    this.page.pageNumber = 0;
    this.page.size = 10;
    storageService.IsLoadedSubject.subscribe(x => { 
      if (!this.isLoaded && x) {
        this.getFarmers();
        this.getCounties();
        this.getWatersheds();
        this.isLoaded = true;
      }
    });
  }

  ionViewWillEnter() {
    if (this.isLoaded) {
      this.getFarmers();
    }
  }

  ngOnInit() {
    this.setPage({ offset: 0 });
  }

  clearFilters() {
    this.name = undefined;
    this.hasPassedCertification = undefined;
    this.countyIds = [];
    this.watershedIds = [];
    this.hasProfileMissingFields = undefined;
    this.hasProfileErrors = undefined;
    this.getFarmers();
  }

  search() {
    this.page.pageNumber = 0; // in case they are on another page and search filters to only one page
    this.getFarmers();
  }

  getFarmers() {
    this.farmerSvc.getAll(
      this.name,
      this.hasPassedCertification,
      true,
      this.watershedIds,
      this.countyIds,
      this.hasProfileMissingFields,
      this.hasProfileErrors,
      this.sortTerm + ' ' + this.sortDirection,
      this.page.pageNumber * this.page.size,
      this.page.size
    ).subscribe(response => {
      this.rows = response.items;
      this.page.totalElements = response.totalCount;
      this.page.totalPages = Math.ceil(response.totalCount / this.page.size);
    });
  }

  getCounties() {
    this.countySvc.getAll().subscribe(counties => this.counties = counties);
  }

  getWatersheds() {
    this.watershedSvc.getAll().subscribe(watersheds => this.watersheds = watersheds);
  }

  newFarmer() {
    this.router.navigate(['/main/farmer']);
  }

  editFarmer(id) {
    const navigationExtras: NavigationExtras = {
      queryParams: {
        farmerId: id
      }
    };
    this.router.navigate(['/main/farmer'], navigationExtras);
  }

  async deleteFarmer(id) {
    const alert = await this.alertController.create({
      header: 'Delete',
      message: 'Are you sure you want to delete this Farmer?',
      buttons: [
        {
          text: 'Cancel',
          handler: () => {
            console.log('Confirm Okay');
          }
        },
        {
          text: 'Delete',
          cssClass: 'danger',
          handler: (blah) => {
            this.farmerSvc.delete(id).subscribe(() => {
              this.rows = this.rows.filter(x => x.id != id);
            });
          }
        }
      ]
    });

    await alert.present();
  }

  isAdmin() {
    return this.permissionChecker.isGranted("Role.Admin");
  }

  async export() {
    const loading = await this.loadingController.create({
      message: 'Please wait...'
    });
    await loading.present();
    this.farmerSvc.getAll(
      this.name,
      this.hasPassedCertification,
      true,
      this.watershedIds,
      this.countyIds,
      this.hasProfileMissingFields,
      this.hasProfileErrors,
      this.sortTerm + ' ' + this.sortDirection,
      0,
      2147483647
    ).subscribe(response => {
      const request = new ExportRequestDto();
      request.entity = 'Farmers';
      request.columnNames = [
        'First Name',
        'Last Name',
        'User Name',
        'Email Address',
        'Phone Number',
        'Farm',
        'County(s)',
        'Watershed(s)',
        'Pass/Fail',
        'Profile Missing Fields',
        'Profile Errors',
        'Last Modified',
        'Last Modified User'
      ];
      request.data = response.items.map(row => {
        return [
          row.userFirstName,
          row.userLastName,
          row.userUserName,
          row.userEmailAddress,
          row.userPhoneNumber,
          row.farmName,
          row.countyNames,
          row.watershedNames,
          row.hasPassedCertification == 1 ? 'Pass' : row.hasPassedCertification == 0 ? 'Fail' : 'Incomplete',
          row.profileMissingFields,
          row.profileErrors,
          row.lastModificationTime ? moment(row.lastModificationTime).format('MM/DD/YYYY') : '',
          row.lastModifierUserName
        ];
      });
      this.exportService.export(request, loading);
    });
  }

  setPage(pageInfo) {
    this.page.pageNumber = pageInfo.offset;
    this.getFarmers();
  }

  onSort(event) {
    this.sortTerm = event.column.prop;
    this.sortDirection = event.newValue.toUpperCase();
    this.getFarmers();
  }

  async onActivate(event) {
    if(event.type == 'click' && event.column.cellClass != 'editDeleteCell') {
      // console.log(event.row);
      const modal = await this.modalController.create({
        component: ProfilePage,
        componentProps: {
          'profile': event.row,
        }
      });
      modal.onDidDismiss().then((param) => {
        if (param && param.data && param.data['doEdit'] == true) {
          this.editFarmer(param.data['farmer'].id);
        } else {
          // this.datatable.selected = [param.data['farmer']];
          // this.selected = [param.data['farmer']];
          // this.cdr.detectChanges();
          this.getFarmers();
        }
      });
      return await modal.present();
    }
  }

  async presentAlert(msg) {
    const alert = await this.alertController.create({
      header: '',
      message: msg,
      buttons: ['OK']
    });

    await alert.present();
  }

}
